import EmptyState from '@aurora/shared-client/components/common/EmptyState/EmptyState';
import type { ListVariantTypeAndProps } from '@aurora/shared-client/components/common/List';
import { ListVariant } from '@aurora/shared-client/components/common/List/enums';
import { PagerVariant } from '@aurora/shared-client/components/common/Pager/enums';
import type { PagerVariantTypeAndProps } from '@aurora/shared-client/components/common/Pager/types';
import { PanelType } from '@aurora/shared-client/components/common/Panel/enums';
import AppContext from '@aurora/shared-client/components/context/AppContext/AppContext';
import type {
  BoardPagesAndParams,
  EndUserRouteAndParams
} from '@aurora/shared-client/routes/endUserRoutes';
import useEndUserRoutes from '@aurora/shared-client/routes/useEndUserRoutes';
import type {
  Board,
  MessageConstraints
} from '@aurora/shared-generated/types/graphql-schema-types';
import type { EndUserPages } from '@aurora/shared-types/pages/enums';
import { EndUserComponent, EndUserQueryParams } from '@aurora/shared-types/pages/enums';
import { TextAlignment } from '@aurora/shared-types/texts/enums';
import { getAsEnum } from '@aurora/shared-utils/helpers/objects/EnumHelper';
import React, { useContext } from 'react';
import MessageListWidgetHelper from '../../../../helpers/messages/MessageHelper/MessageListWidgetHelper';
import { buildMessagesConstraintsForNode } from '../../../../helpers/nodes/NodeHelper/NodeHelper';
import { ItemType, MessageTimestamp, MessageViewVariant } from '../../../../types/enums';
import type {
  AllMessageViewsQuery,
  AllMessageViewsQueryVariables,
  AllMessageViewFragment
} from '../../../../types/graphql-types';
import PaneledItemList from '../../../common/List/PaneledItemList/PaneledItemList';
import type { ItemViewTypeAndProps } from '../../../entities/types';
import useEntityViewQuery from '../../../useEntityViewQuery';
import useQueryParamValueForWidget from '../../../useQueryParamValueForWidget';
import useTranslation from '../../../useTranslation';
import { MessageListTabItem, messageListTabItemMap } from '../../MessageListTabs/MessageListTabs';
import MessagesSortOptions from '../../MessagesSortOptions/MessagesSortOptions';
import messagesQuery from './AllMessageViews.query.graphql';
import PaneledItemListSubHeader from '../../../common/List/PaneledItemListSubHeader/PaneledItemListSubHeader';
import localStyles from '../MessageListForViewAllWidget.module.pcss';
import { useClassNameMapper } from 'react-bootstrap';

interface Props {
  /**
   * The view variant information
   */
  viewVariant?: ItemViewTypeAndProps<ItemType.MESSAGE, MessageViewVariant>;
  /**
   * The list variant information
   */
  listVariant?: ListVariantTypeAndProps<AllMessageViewFragment>;
  /**
   * The pager variant type
   */
  pagerVariant?: PagerVariantTypeAndProps<EndUserPages, EndUserQueryParams>;
  /**
   * The page size
   */
  pageSize?: number;
  /**
   * The type of panel
   */
  panelType?: PanelType;
  /**
   * The list of sort options to render in dropdown
   */
  sortOptions?: MessageListTabItem[];
  /**
   * The query variables
   */
  queryVariables?: AllMessageViewsQueryVariables;
}

/**
 * Component to render the list of messages for View All page
 *
 * @author Manasvini Arul
 * @constructor
 */
const AllMessagesList: React.FC<React.PropsWithChildren<Props>> = ({
  viewVariant = {
    type: MessageViewVariant.INLINE,
    props: {
      ...MessageListWidgetHelper.getAllMessageViewCommonDefaults()
    }
  },
  listVariant = {
    type: ListVariant.LIST_GROUP
  },
  pagerVariant = {
    type: PagerVariant.PREVIOUS_NEXT_LINKABLE
  },
  pageSize = 10,
  panelType = PanelType.DIVIDER,
  queryVariables,
  sortOptions
}) => {
  const cx = useClassNameMapper(localStyles);
  const { contextNode } = useContext(AppContext);
  let constraints: MessageConstraints = buildMessagesConstraintsForNode(contextNode);
  const { router } = useEndUserRoutes();
  const { formatMessage, loading: textLoading } = useTranslation(
    EndUserComponent.MESSAGE_LIST_FOR_VIEW_ALL_WIDGET
  );
  const contextBoard = contextNode as Board;
  const beforeCursorValue = router.getQueryParam(EndUserQueryParams.BEFORE);
  const afterCursorValue = router.getQueryParam(EndUserQueryParams.AFTER);
  const { key: sortParamKey, value: sortParamValue } = useQueryParamValueForWidget(
    EndUserQueryParams.MESSAGE_LIST_MENU_QUERY_PARAMETER,
    ''
  );
  const sortValue = getAsEnum(sortParamValue, MessageListTabItem, sortOptions[0]);
  const sortForWidget = messageListTabItemMap[sortValue].sorts;

  let updatedViewVariant = viewVariant;
  if (sortValue === MessageListTabItem.NEWEST) {
    updatedViewVariant = {
      ...viewVariant,
      props: { ...viewVariant.props, timeStampType: MessageTimestamp.POST_TIME }
    };
  }

  constraints = {
    depth: { eq: 0 },
    conversationStyle: { eq: contextBoard.conversationStyle },
    ...constraints,
    ...queryVariables?.constraints
  };

  const variables = {
    ...queryVariables,
    constraints: constraints,
    sorts: sortForWidget,
    useFullPageInfo: true,
    ...(beforeCursorValue && { before: beforeCursorValue as string, last: pageSize }),
    ...(afterCursorValue && { after: afterCursorValue as string, first: pageSize }),
    ...(!afterCursorValue && !beforeCursorValue && { first: pageSize })
  };

  const queryResult = useEntityViewQuery<
    ItemType.MESSAGE,
    AllMessageViewsQuery,
    AllMessageViewsQueryVariables
  >(module, ItemType.MESSAGE, viewVariant, messagesQuery, {
    variables
  });

  if (textLoading) {
    return null;
  }

  /**
   * Function to get the sort option text
   * @param value the sort option
   */
  function getSortOptionText(value: MessageListTabItem): string {
    return value === MessageListTabItem.MOST_KUDOED || value === MessageListTabItem.NEWEST
      ? formatMessage(value, { value: contextBoard.conversationStyle })
      : formatMessage(value);
  }

  const sortFilters = sortOptions.map(option => ({
    key: option,
    value: getSortOptionText(option)
  }));

  /**
   * A function to render Empty State when entity list has no messages
   */
  function renderNoPosts(): React.ReactElement {
    return (
      <EmptyState description={formatMessage('emptyDescription')} alignment={TextAlignment.LEFT} />
    );
  }

  /**
   * Function to render when sort value is changed
   * @param item the selected sort item
   */
  function onSortChange(item: MessageListTabItem): void {
    router.replaceRoute<EndUserRouteAndParams<EndUserPages>>(
      router.getCurrentPageName(),
      router.getPathParams<BoardPagesAndParams>(),
      {
        ...router.getQueryParams(),
        ...(afterCursorValue && { [EndUserQueryParams.AFTER]: null }),
        ...(beforeCursorValue && { [EndUserQueryParams.BEFORE]: null }),
        [sortParamKey]: item
      },
      { scroll: false }
    );
  }

  /**
   * Function to render the filter bar
   */
  const renderFilterBar: React.FC = () => {
    return (
      <PaneledItemListSubHeader panel={panelType} as={'nav'} className={cx('lia-filter-bar')}>
        <MessagesSortOptions
          selectedSortFilterValue={getSortOptionText(sortValue)}
          sortFilters={sortFilters}
          onSortFilterChange={onSortChange}
          className={cx('lia-sort-dropdown')}
        />
      </PaneledItemListSubHeader>
    );
  };

  return (
    <PaneledItemList<
      AllMessageViewFragment,
      ItemType.MESSAGE,
      ItemViewTypeAndProps<ItemType.MESSAGE, MessageViewVariant>,
      AllMessageViewsQuery,
      AllMessageViewsQueryVariables
    >
      type={ItemType.MESSAGE}
      variant={updatedViewVariant}
      listVariant={listVariant}
      queryResult={queryResult}
      panel={panelType}
      pagerVariant={pagerVariant}
      pageSize={pageSize}
      empty={renderNoPosts}
      useEmpty
      id="AllMessagesList"
      subHeader={renderFilterBar}
    />
  );
};

export default AllMessagesList;
